import { FC, useCallback, useEffect, useState } from 'react'
import { Box, Grid, Tab, Tabs } from '@mui/material'
import { useSearchParams } from 'react-router-dom'
import { PageHeader } from '@/components/common/PageHeader'
import { KpiCard } from '@/components/common/KpiCard'
import { EstadoDeLista } from '@/components/common/EstadoDeLista'
import { resumoService } from '@/services/resumoService'
import { ComissoesResumoConsultor } from '@/types/resumos'
import { authStore } from '@/store/authStore'
import { ehConsultor, temPapelFinanceiro } from '@/components/layout/menu'
import ComissoesPanel from './ComissoesPanel'
import NotasFiscaisPanel from './NotasFiscaisPanel'
import PoliticaNfPanel from './PoliticaNfPanel'
import CompetenciasPanel from './CompetenciasPanel'
import PoliticasRetencaoPanel from './PoliticasRetencaoPanel'

type Aba = 'comissoes' | 'nf' | 'parametros-nf' | 'competencias' | 'retencoes'

const formatBRL = (v: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0)

/** Regra do contrato: valor nulo vira "—" — nada é calculado (nem inventado) na tela. */
const brlOuTraco = (v: number | null | undefined) => (v == null ? '—' : formatBRL(v))

/**
 * Financeiro — comissões do consultor (PDF, Etapa 17; Figma 26:339, Onda 2 / A04).
 *
 * A comissão é MANUAL (decisão de 04/08): o operador lança, nada é calculado aqui. Os
 * quatro KPIs vêm de `GET /v1/comissoes/resumo-consultor` (A03) — a mesma fonte do
 * extrato listado abaixo. `aReceber` (confirmada SEM nota) e `aguardandoNf` (confirmada
 * COM nota enviada) são disjuntos por desenho: somá-los não conta nada duas vezes.
 *
 * Unificada pelo A15 (Onda 8): a tabela ganhou as colunas do Figma e o status derivado
 * (Aguardando NF → NF recebida → Programado → Pago); a antiga `/notas-fiscais` virou a
 * aba `?aba=nf` desta página (`router/rotas/a15.tsx` redireciona).
 *
 * P5 (AN-PP5 §1.3, §9): abas "Competências" (fechar/reabrir) e "Políticas de retenção" para o
 * back-office e o papel complementar FINANCEIRO — nunca para a persona consultor (mesmo com
 * FINANCEIRO/OPERADOR residual). Fechar e publicar retenção: FINANCEIRO ou ADMIN; reabrir: só ADMIN
 * (o backend confere de novo). O usuário "só FINANCEIRO" não lê extrato, notas nem o resumo do
 * consultor no backend (CONSULTOR_OR_ABOVE / OPERADOR_OR_ABOVE): para ele a página abre nas
 * Competências e mostra só as abas que o backend libera.
 */
const FinanceiroPage: FC = () => {
  const usuario = authStore((s) => s.usuario)
  // Persona pelo critério único do menu: CONSULTOR com OPERADOR residual é consultor.
  const consultor = ehConsultor(usuario?.permissoes)
  // Confirmar recebimento e cancelar são atos do back-office; o consultor só acompanha.
  const podeOperar =
    !consultor &&
    !!usuario?.permissoes?.some((p) => ['ADMIN', 'GERENTE', 'OPERADOR'].includes(p.toUpperCase()))

  // P5 (AN-PP5 §1.3): papel complementar FINANCEIRO (nunca a persona consultor).
  const financeiro = temPapelFinanceiro(usuario?.permissoes)
  const admin = !consultor && !!usuario?.permissoes?.some((p) => p.toUpperCase() === 'ADMIN')

  // AN-9 (Q-AN9-3) + V64 (AN-PP6 §3.2): parâmetros da NF — FINANCEIRO/GERENTE/ADMIN propõem, OPERADOR só lê;
  // aprovar/rejeitar a proposta é do FINANCEIRO ou ADMIN (e nunca de quem propôs — a tela desabilita).
  const podeEditarPoliticaNf =
    financeiro ||
    (podeOperar && !!usuario?.permissoes?.some((p) => ['ADMIN', 'GERENTE'].includes(p.toUpperCase())))
  const podeAprovarPoliticaNf = financeiro || admin
  /** Competências e retenções: leitura do back-office ou do FINANCEIRO. */
  const veFinanceiroP5 = podeOperar || financeiro
  /** Só FINANCEIRO: o backend não libera extrato, notas nem o resumo do consultor para ele. */
  const somenteFinanceiro = financeiro && !podeOperar
  const podeFecharCompetencia = financeiro || admin
  const podeEditarRetencao = financeiro || admin
  /** Parâmetros da NF: back-office lê; o FINANCEIRO também (aprova a proposta que vê). */
  const veParametrosNf = podeOperar || financeiro

  const [searchParams, setSearchParams] = useSearchParams()
  const abaUrl = searchParams.get('aba')
  const abaPadrao: Aba = somenteFinanceiro ? 'competencias' : 'comissoes'
  const aba: Aba =
    abaUrl === 'nf' && !somenteFinanceiro
      ? 'nf'
      : abaUrl === 'parametros-nf' && veParametrosNf
        ? 'parametros-nf'
        : abaUrl === 'competencias' && veFinanceiroP5
          ? 'competencias'
          : abaUrl === 'retencoes' && veFinanceiroP5
            ? 'retencoes'
            : abaPadrao
  const trocarAba = (nova: Aba) => {
    const params = new URLSearchParams(searchParams)
    if (nova !== abaPadrao) params.set('aba', nova)
    else params.delete('aba')
    if (nova !== 'nf') params.delete('nota')
    setSearchParams(params, { replace: true })
  }
  // Linha do extrato em NF_COM_PENDENCIA → a nota na aba de NF (mesmo destino da Central).
  const abrirNota = (notaId: string) => {
    const params = new URLSearchParams(searchParams)
    params.set('aba', 'nf')
    params.set('nota', notaId)
    params.delete('lancamento')
    params.delete('estado')
    setSearchParams(params, { replace: true })
  }
  // Deep link da Central (L7 / Q40): `?lancamento=<id>` ou `?estado=AGUARDANDO_NF` destaca
  // a(s) linha(s) no extrato. Só o estado da NF é aceito — URL é entrada de usuário.
  const lancamentoDestacado = searchParams.get('lancamento')
  const estadoDestacado = searchParams.get('estado') === 'AGUARDANDO_NF' ? 'AGUARDANDO_NF' : null
  // Uma nota paga/recusada na aba de NF muda o estado das linhas do extrato.
  const [versaoExtrato, setVersaoExtrato] = useState(0)

  const [resumo, setResumo] = useState<ComissoesResumoConsultor | null>(null)
  const [carregando, setCarregando] = useState(true)
  const [erro, setErro] = useState<string | null>(null)
  const [erroStatus, setErroStatus] = useState<number | null>(null)

  const carregar = useCallback(async () => {
    // Só FINANCEIRO: o resumo do consultor é CONSULTOR_OR_ABOVE no backend — não chama para não dar 403.
    if (somenteFinanceiro) {
      setCarregando(false)
      return
    }
    setCarregando(true)
    setErro(null)
    setErroStatus(null)
    try {
      setResumo(await resumoService.comissoesConsultor())
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Falha ao carregar o resumo de comissões')
      setErroStatus((e as { status?: number })?.status ?? null)
    } finally {
      setCarregando(false)
    }
  }, [somenteFinanceiro])

  useEffect(() => {
    void carregar()
  }, [carregar])

  return (
    <Box>
      <PageHeader
        title="Financeiro"
        subtitle={
          somenteFinanceiro
            ? 'Fechamento de competência e políticas de retenção da NF'
            : 'Comissões: a receber, aguardando nota fiscal, pagas e recorrentes'
        }
      />

      {somenteFinanceiro ? null : erro ? (
        <EstadoDeLista
          estado={erroStatus === 403 ? 'semPermissao' : 'erro'}
          {...(erroStatus !== 403 && {
            mensagem: 'Não foi possível carregar o resumo de comissões',
            detalhe: erro,
            acao: { label: 'Tentar novamente', onClick: () => void carregar() },
          })}
          minHeight={160}
        />
      ) : (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            {/* fonte: GET /v1/comissoes/resumo-consultor (aReceber — confirmada SEM nota) */}
            <KpiCard
              label="A receber"
              valor={brlOuTraco(resumo?.aReceber)}
              legenda="Disponível para emitir NF"
              variante="escura"
              carregando={carregando}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            {/* fonte: GET /v1/comissoes/resumo-consultor (aguardandoNf — nota ENVIADA).
                VC-03: rótulo "NF enviada" — "Aguardando NF" é o chip do extrato (confirmada
                SEM nota, `estadoFinanceiro.ts`); os dois sentidos não dividem o nome. */}
            <KpiCard
              label="NF enviada"
              valor={brlOuTraco(resumo?.aguardandoNf)}
              legenda="aguardando pagamento"
              carregando={carregando}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            {/* fonte: GET /v1/comissoes/resumo-consultor (pagoNoAno — notas PAGAS no ano) */}
            <KpiCard
              label="Pago no ano"
              valor={brlOuTraco(resumo?.pagoNoAno)}
              carregando={carregando}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            {/* fonte: GET /v1/comissoes/resumo-consultor (recorrente — recorte transversal) */}
            <KpiCard
              label="Recorrente"
              valor={brlOuTraco(resumo?.recorrente)}
              legenda="Receita que se repete"
              carregando={carregando}
            />
          </Grid>
        </Grid>
      )}

      <Tabs
        value={aba}
        onChange={(_, v: Aba) => trocarAba(v)}
        sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
        aria-label="Seções do Financeiro"
      >
        {!somenteFinanceiro && <Tab value="comissoes" label="Comissões" />}
        {!somenteFinanceiro && <Tab value="nf" label="Notas fiscais" />}
        {veParametrosNf && <Tab value="parametros-nf" label="Parâmetros da NF" />}
        {veFinanceiroP5 && <Tab value="competencias" label="Competências" />}
        {veFinanceiroP5 && <Tab value="retencoes" label="Políticas de retenção" />}
      </Tabs>

      {aba === 'competencias' ? (
        <CompetenciasPanel podeFechar={podeFecharCompetencia} podeReabrir={admin} />
      ) : aba === 'retencoes' ? (
        <PoliticasRetencaoPanel podeEditar={podeEditarRetencao} />
      ) : aba === 'parametros-nf' ? (
        <PoliticaNfPanel podeEditar={podeEditarPoliticaNf} podeAprovar={podeAprovarPoliticaNf} />
      ) : aba === 'nf' ? (
        <NotasFiscaisPanel
          podeOperar={podeOperar}
          consultor={consultor}
          onMudou={() => {
            setVersaoExtrato((v) => v + 1)
            void carregar()
          }}
        />
      ) : (
        <ComissoesPanel
          podeOperar={podeOperar}
          versao={versaoExtrato}
          lancamentoDestacado={lancamentoDestacado}
          estadoDestacado={estadoDestacado}
          onAbrirNota={abrirNota}
        />
      )}
    </Box>
  )
}

export default FinanceiroPage
